/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `NavigationContext` for the Caintra Connect frontend.
 * @module context/NavigationContext
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { createContext, useContext, useState, useCallback } from "react";

const NavigationContext = createContext(null);

export const NavigationProvider = ({ children }) => {
  const [navigationState, setNavigationState] = useState({
    currentRoute: "",
    context: "", // 'myCompany', 'directory', etc.
    previousRoute: "",
    previousContext: "",
  });

  const updateNavigation = useCallback((route, context) => {
    setNavigationState((prev) => ({
      currentRoute: route,
      context: context,
      previousRoute: prev.currentRoute,
      previousContext: prev.context,
    }));
  }, []);

  const value = {
    navigationState,
    updateNavigation,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
